import React, { useState, useEffect } from "react";
import { Table, Select, Pagination } from "antd";
import ReferalsService from "../../../services/Referals.services";
import { setError } from "../../../store/reducers";
import { useDispatch } from "react-redux";
import localStorageContent from "../../../utils/localstorage";
import SearchFilter from "../../../components/SearchFilter";
import DownloadCsv from "../../../utils/downloadCSV";
import moment from "moment";

// Define a more complete interface for Referal data
interface Referal {
  // 'id' will be our internal unique key, derived from 'referal_id'
  id: string; // Crucial for rowKey and state management
  name: string;
  phone?: string;
  email?: string;
  team_member?: string;
  updated_dt?: string;
  referred_by?: string;
  status?: string | null;
  followup_comment?: string;
  referal_id?: number; // The original ID from your backend data (number type)
  created_by?: number;
  created_dt?: string;
  lead_id?: number | null;
  team_id?: number;
}

// Props interface for ReferalList for better type safety
interface ReferalListProps {
  referals: any[]; // The raw data coming from the parent (will be transformed)
  getReferals: (page: number, queryString: string | null) => void;
  totalCount: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  setQueryString: (queryString: string | null) => void;
}

function ReferalList({
  referals,
  getReferals,
  totalCount,
  currentPage,
  setCurrentPage,
  setQueryString,
}: ReferalListProps) {
  const [queryStringList, setQueryStringList] = useState<any>(null);
  const { editReferal } = ReferalsService;
  const { Option } = Select;
  const localUserData = localStorageContent.getUserData();
  const dispatch = useDispatch();

  // State to hold the transformed referals data with the 'id' property
  const [transformedReferals, setTransformedReferals] = useState<Referal[]>([]);

  // State to track the ID of the single row currently being edited
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  // State to hold comments, keyed by the 'id' (which is now referal_id as string)
  const [comments, setComments] = useState<{ [key: string]: string }>({});

  // Effect to transform the incoming 'referals' prop into 'transformedReferals'
  // This ensures each item has a consistent 'id' property for React's keys and our state management.
  useEffect(() => {
    if (referals && Array.isArray(referals) && referals.length > 0) {
      const newTransformed: Referal[] = referals.map((item: any) => ({
        ...item,
        // CRITICAL FIX: Map backend's 'referal_id' to our frontend's 'id'
        id: String(item.referal_id), // Ensure it's a string for consistent keys
      }));
      setTransformedReferals(newTransformed);

      // Initialize comments state with existing followup_comments if they are available
      // and not already in our local 'comments' state.
      const initialComments: { [key: string]: string } = {};
      newTransformed.forEach((item) => {
        if (item.id && item.followup_comment && !comments[item.id]) {
          initialComments[item.id] = item.followup_comment;
        }
      });
      // Only update if there are new comments to add to avoid unnecessary re-renders
      if (Object.keys(initialComments).length > 0) {
        setComments((prev) => ({ ...prev, ...initialComments }));
      }
    } else {
      setTransformedReferals([]);
      setComments({}); // Clear comments if no referals
    }
  }, [referals]); // Re-run this effect when the 'referals' prop changes

  const options = [
    { value: "0", label: "Pending" },
    { value: "1", label: "Interested" },
    { value: "2", label: "Not Interested" },
    { value: "3", label: "Followup" },
  ];

  const updateReferalStatus = async (value: string, referal: Referal) => {
    // Use the transformed 'id' for local state management (comments, editingRowId)
    const idKey = referal.id;
    // Get the comment from the local state for this specific row
    const comment = comments[idKey]?.trim();

    try {
      // Use the original 'referal_id' for the API call as your backend expects it
      if (referal?.referal_id) {
        const payload: any = { status: value };
        if (value === "3") {
          // If status is 'Followup', include the comment
          payload.comment = comment;
        }

        const response = await editReferal(
          `/referral/${referal.referal_id}/update-status`, // API uses referal_id
          payload
        );
        dispatch(
          setError({
            status: true,
            type: "success",
            message: response?.message,
          })
        );

        // Re-fetch data, ideally staying on the current page if possible,
        // but your original logic fetches page 1.
        getReferals(Number(currentPage) || 1, queryStringList);
        setEditingRowId(null); // Exit editing mode for the row after successful save
      }
    } catch (err: any) {
      // Improved error message dispatching
      dispatch(
        setError({
          status: true,
          type: "error",
          message: err?.message || "An error occurred.",
        })
      );
    }
  };

  const genarateStatus = (status: string | null | undefined) => {
    let currentStatus = "";
    switch (
      status // Use 'status' directly with a strict switch
    ) {
      case null:
      case "0": // Assuming "0" also maps to Pending from your searchFields
        currentStatus = "Pending";
        break;
      case "1":
        currentStatus = "Interested";
        break;
      case "2":
        currentStatus = "Not Interested";
        break;
      case "3":
        currentStatus = "Followup";
        break;
      default:
        currentStatus = "--";
    }
    return currentStatus;
  };

  const exportFormates: any = {
    name: (value: any) => (value ? value : ""),
    status: (value: any) => (value ? genarateStatus(value) : ""),
  };

  const handleCommentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    record: Referal
  ) => {
    const idKey = record.id; // Use the transformed 'id' for comment state
    const value = e.target.value;

    setComments((prev) => ({
      ...prev,
      [idKey]: value,
    }));
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      dataIndex: "name", // Directly use dataIndex if it matches
    },
    {
      title: "Number",
      dataIndex: "phone",
      key: "phone",

      render: (phone: string) => {
        const cleanPhone = phone
          ?.replace(/[^\d+]/g, "") // keep only digits and '+' sign
          .trim();
        return cleanPhone;
      },
    },
    {
      title: "Email ID",
      dataIndex: "email",
      key: "email",

      render: (email: string) => {
        const cleanEmail = email
          ?.replace(/[^\x20-\x7E]/g, "") // Remove non-ASCII characters
          .trim();
        return cleanEmail;
      },
    },
    {
      title: "Created By",
      dataIndex: "team_member",
      key: "team_member",
    },
    {
      title: "Updated Date",
      dataIndex: "updated_dt",
      key: "updated_dt",
      render: (date: any) => (date ? moment(date).format("YYYY-MM-DD") : "--"),
    },
    {
      title: "Refered By",
      dataIndex: "referred_by",
      key: "referred_by",
    },
    {
      title: "Status",
      key: "status",
      render: (record: Referal) => {
        // Use 'record' for clarity
        return <>{genarateStatus(record.status)}</>;
      },
    },
    // Conditionally add the 'Actions' column if the user's role is not '5'
    ...(localUserData && localUserData.role !== "5"
      ? [
          {
            title: "Actions",
            key: "actions", // Add a unique key for the column
            render: (record: Referal) => {
              // Use 'record' for clarity
              // Determine the current selected value for the Ant Design Select
              // Map null status to "0" (Pending) for consistency with options
              const currentSelectValue =
                record.status === null ? "0" : record.status;
              const selectedOption = options.find(
                (opt) => opt.value === currentSelectValue
              );

              return (
                <div>
                  <Select
                    style={{ width: 150 }}
                    // Value should be controlled and mapped to the status
                    value={selectedOption ? selectedOption.value : "0"} // Default to "0" (Pending) if not found
                    onChange={(value: string) =>
                      updateReferalStatus(value, record)
                    }
                  >
                    {/* Explicitly add a "Pending" option if your data can have null status */}
                    {options.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: "Comment",
      key: "followup_comment",
      render: (_text: any, record: Referal) => {
        const idKey = record.id; // Use the transformed 'id' for this row

        // Check if THIS row's ID matches the single 'editingRowId'
        const isEditingThisRow = editingRowId === idKey;

        // Use comment from local state if available, otherwise from record, default to empty string
        const commentValue = comments[idKey] ?? record.followup_comment ?? "";

        // Only show input and buttons if status is 'Followup' ("3")
        return record.status === "3" ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="text"
              placeholder="Enter comment"
              value={commentValue}
              disabled={!isEditingThisRow} // Input is disabled unless this specific row is in edit mode
              onChange={(e) => handleCommentChange(e, record)}
              style={{ width: 200 }}
            />
            {isEditingThisRow ? (
              <button
                onClick={() => {
                  updateReferalStatus("3", record); // Call to save the comment
                  // setEditingRowId(null) is now handled inside updateReferalStatus
                }}
              >
                Save
              </button>
            ) : (
              <button
                onClick={() => setEditingRowId(idKey)} // Set this row's ID as the one being edited
              >
                Edit
              </button>
            )}
          </div>
        ) : (
          record.followup_comment || "--" // Display comment if not status "3" or no comment
        );
      },
    },
  ];

  const searchFields = [
    {
      label: "Client Search",
      key: "name",
      elementType: "INPUT",
      required: true,
      disable: false,
      onChangeField: () => {},
      type: "text",
      placeholder: "Search User",
      config: {
        rules: [{ required: false, message: "Please Enter something" }],
      },
    },
    {
      label: "Status",
      key: "status",
      elementType: "SELECT",
      onChangeField: () => {},
      options: [
        {
          value: "0",
          label: "Pending",
        },
        {
          value: "1",
          label: "Interested",
        },
        {
          value: "2",
          label: "Not Interested",
        },
      ],
      required: true,
      disable: false,
      type: "string",
      placeholder: "Select status",
      config: {
        rules: [{ required: false, message: "Please select status" }],
      },
    },
  ];

  return (
    <div>
      <SearchFilter
        fields={searchFields}
        onSubmit={(queryStrings: any) => {
          setCurrentPage(1);
          setQueryString(queryStrings);
          setQueryStringList(queryStrings);
        }}
        clearSearch={() => {
          setCurrentPage(1);
          setQueryString(null);
          setQueryStringList(null);
        }}
        showButtons={true}
        colVal={4}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {localUserData && localUserData?.role === "1" && (
          <DownloadCsv
            // Only map columns that have a 'key' property to be included in CSV headers
            headers={columns
              .filter((col) => col.key)
              .map((col: any) => ({
                title: col.title,
                key: col.key,
                // Check if 'exportFormates' has a custom formatter for this column's key
                formate: exportFormates[col.key] || false,
              }))}
            filename={"Referals"}
            URL={
              queryStringList
                ? `/referral?download=1&${queryStringList}`
                : `/referral?download=1`
            }
            disabled={false}
          />
        )}
        <p>
          <strong>Total Records : {totalCount}</strong>
        </p>
      </div>
      <Table
        pagination={false}
        columns={columns.slice(0, 9)} // Ensure you're slicing to include the correct number of columns
        dataSource={transformedReferals} // Use the transformed data source
        rowKey="id" // Crucial: tells Ant Design Table to use the 'id' property as the unique key
      />
      <Pagination
        onChange={(pagination: number) => {
          // Type 'pagination' as number
          setCurrentPage(pagination);
        }}
        style={{ marginTop: "15px" }}
        current={currentPage}
        defaultPageSize={25}
        showSizeChanger={false}
        hideOnSinglePage
        total={totalCount}
      />
    </div>
  );
}

export default ReferalList;
